import { Heart } from "lucide-react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Price } from "@/components/ui/price";
import { Skeleton } from "@/components/ui/skeleton";
import { PUBLIC_ROUTES } from "@/constants/routes";
import type { Product, ProductCondition } from "@/features/marketplace/types/product";
import { cn } from "@/utils/cn";
import { formatRelativeTime } from "@/utils/formatRelativeTime";

const CONDITION_LABEL: Record<ProductCondition, string> = {
  new: "New",
  "like-new": "Like New",
  used: "Used",
  "for-parts": "For Parts",
};

interface ProductCardProps {
  product: Product;
  isFavorite?: boolean;
  /** Omit entirely to render without the favorite toggle — the wishlist
   * feature isn't built yet, so a card with nothing to wire it to doesn't
   * render a dead affordance. */
  onFavoriteToggle?: (productId: string) => void;
}

/**
 * The Grid variant from docs/ui/component-inventory.md's `ProductCard` —
 * the only one built so far, since it's the only one anything (Home)
 * currently needs. List/Compact land once Search/Wishlist need them.
 */
export function ProductCard({ product, isFavorite = false, onFavoriteToggle }: ProductCardProps) {
  const isSold = product.status === "sold";
  const detailHref = PUBLIC_ROUTES.productDetails.replace(":slug", product.slug);

  return (
    <div className="group relative overflow-hidden rounded-lg border border-border bg-card shadow-sm transition-shadow hover:shadow-md">
      <Link to={detailHref} className="block">
        <div className="relative aspect-square w-full overflow-hidden bg-muted">
          {product.images[0] ? (
            <img
              src={product.images[0]}
              alt={product.title}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-caption text-muted-foreground">
              No image
            </div>
          )}

          <Badge variant="neutral" className="absolute left-2 top-2">
            {CONDITION_LABEL[product.condition]}
          </Badge>

          {isSold && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/70">
              <Badge variant="neutral">Sold</Badge>
            </div>
          )}
        </div>

        <div className="space-y-1 p-4">
          <Price value={product.price} originalValue={product.originalPrice} size="body-lg" />
          <p className="truncate text-body text-foreground">{product.title}</p>
          <p className="text-body-sm text-muted-foreground">
            {product.location.city}, {product.location.state}
          </p>
          <p className="text-caption text-muted-foreground">
            {formatRelativeTime(product.createdAt)}
          </p>
        </div>
      </Link>

      {onFavoriteToggle && (
        <button
          type="button"
          aria-label={
            isFavorite
              ? `Remove ${product.title} from wishlist`
              : `Add ${product.title} to wishlist`
          }
          onClick={(event) => {
            event.preventDefault();
            onFavoriteToggle(product.id);
          }}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm hover:bg-background"
        >
          <Heart
            className={cn("h-4 w-4", isFavorite && "fill-destructive text-destructive")}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
      <Skeleton className="aspect-square w-full rounded-none" />
      <div className="space-y-2 p-4">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
  );
}
